<template lang="pug">
div.manage-taxonomy-tree--container.l-flex-row

    draggable.manage-taxonomy-tree.scrollbar.is-scrollable.l-flex-column(
        v-model="treeList"
        :id="'manage-taxonomy-tree' + scrollId"
        tag="div"
        v-bind="getOptions()"
        @end="onEnd"
        :move="checkMove"
        @change="onChange")

        div.draggable-item(
            v-for="(element, index) in treeList" :key="index"
            :id="element.id === null ? '_' +  element.key : '_' +  element.id"
            :class="[ItemCssClasses(element), isSelectedFromSearch(element)]"
            @click="selectNode(element)"
        )
            div.draggable-item-infos.l-flex-column(:key="element.key")
                div.l-flex-row--between
                    div.l-flex-row.l-align-center
                        svg-icon(
                            v-if="element.smart_segments && element.smart_segments.length > 0"
                            name='period' size="small" )
                        span.draggable-item--infos.id(v-if="element.id != element.key") {{ element.id }}
                    span.draggable-item--infos.key(:title="element.key") {{ element.key }}
                div.l-flex-row--between
                    span.draggable-item--infos.label.css-cut-long-text {{ element.label }}
                    div.draggable-item_action
                        svg-icon.cs-menu-action(name='dots' size="small")
                        div.item_action-menu
                            div.item-action-item(@click.stop="editItem(element)")
                                svg-icon(name='edit' size="small")
                                | edit
                            div.item-action-item(@click.stop="createCustomSegment(element)")
                                svg-icon(name='add' size="small")
                                | create
                            div.item-action-item.item-action_delete(@click.stop="toggleDeleteItem(element)")
                                svg-icon(name='trash' size="small")
                                | {{ toggleDeleteText(element) }}
                            .cs-menu-action-arrow

                    div.item-children-mark
                        svg-icon(v-if="hasChildren(element)" name='arrow_right' size="small")

                div.draggable-item--infos.rule.row-item-status.rule-segment(:class="itemStatus(element)") rule

            draggable.sub-item(
                :data-id="element.id"
                :value="childList(element)"
                @change="onChangeSubItem"
                @add="onAddSubItem"
                tag="div"
                v-bind="getOptions()"
                :key="element.id + element.label")

    ManageTaxonomyTree.children(v-if="displayChildren" :class="{'hidden': hideChildren }" :parent="selectedNode" :treeData="selectedNode.children" :scrollId="selectedNode.id" :key="selectedNode.id + selectedNode.label")
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { scroller } from 'vue-scrollto/src/scrollTo'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { find, get, isEmpty, isEqual, isNil, cloneDeep, toLower, includes, last } from 'lodash'

export default {
    name: 'ManageTaxonomyTree',

    props: {
        treeData: { required: true, type: Array },
        parent: { required: false, type: Object, default: () => ({}) },
        scrollId: { required: true, type: String }
    },

    data() {
        return {
            tree: this.treeData,
            selectedNode: { id: null },
            draggedNode: { id: null },
            added: null,
            scrollOptions: {
                container: '#manage-taxonomy-tree' + this.scrollId,
                easing: 'linear',
                force: true,
                cancelable: true,
                x: false,
                y: true
            }
        }
    },

    computed: {
        ...mapState('ui/manageTaxonomy', {
            dataType: state => state.selectedType,
            displayingSearch: state => state.displayingSearch,
            isSearching: state => state.isSearching,
            searchedCustomSegmentParents: state => state.searchedCustomSegmentParents,
            selectedSegmentId: state => state.selectedSegmentId,
            draggedCounter: state => state.counters.dragged,
            formSegmentAction: state => state.formSegment.type,
            rightPanelActiveTab: state => state.activeTab
        }),

        ...mapGetters('ui/manageTaxonomy', ['getItemById']),

        displayChildren() {
            return !isNil(this.selectedNode.id) && !isEmpty(this.selectedNode.children)
        },

        hideChildren() {
            return isEqual(this.selectedNode.id, this.draggedNode.id)
        },

        parentLabel() {
            return this.parent ? this.parent.label : ''
        },

        parentId() {
            return this.parent ? this.parent.id : ''
        },

        treeList: {
            get: function() {
                // We need to add a property isSaved in order to know if the segment that we are editing has already been saved or is just created
                const reformatedList = []
                for (const item of this.tree) {
                    if (!item.hasOwnProperty('isSaved')) {
                        item.isSaved = true
                    }
                    reformatedList.push(item)
                }
                return reformatedList
            },
            set: function(_value) {
                this.tree = _value
            }
        }
    },

    watch: {
        selectedSegmentId: {
            handler: function(newValue, oldValue) {
                //- no search, when selectedNode parent is clicked => unselect node
                if (!this.isSearching && !this.displayingSearch) {
                    if (!isNil(this.selectedNode.id) && !isNil(this.selectedNode.parent_id)) {
                        if (isEqual(newValue, this.selectedNode.parent_id)) {
                            this.selectedNode = { id: null }
                        }
                    }
                }

                //- search ongoing, user clicked on on cs in searchResults => launch scroll
                if (this.isSearching && !isNil(newValue) && !isEqual(newValue, oldValue)) {
                    if (!isNil(find(this.treeData, item => item.id === newValue || item.key === newValue))) {
                        //- store selected node id IS in tree instance => base VueScroll on store selected node

                        //- get element from store
                        const _cs = this.getItemById({ list: toLower(this.dataType) === 'direct' ? this.directData : this.crmData, id: newValue })
                        const id = isNil(_cs.id) ? _cs.key : _cs.id

                        //- set instance selected node
                        if (!isEqual(this.selectedNode.id, id)) {
                            this.selectedNode = { ..._cs, id }
                        }

                        //- scroll to node
                        VueScrollTo.scrollTo('#_' + id, '100', this.scrollOptions)

                        //- stop search if last elem
                        if (!this.displayingSearch) {
                            this.setIsSearching({ isSearching: false })
                        }
                    } else {
                        //- store selected node id NOT in tree instance=> base VueScroll on instance selected node
                        if (includes(this.searchedCustomSegmentParents, this.selectedNode.id)) {
                            //- instance selected node is ancestor of clicked item
                            //- get element from store
                            const _cs = this.getItemById({
                                list: toLower(this.dataType) === 'direct' ? this.directData : this.crmData,
                                id: this.selectedNode.id
                            })
                            const id = isNil(_cs.id) ? _cs.key : _cs.id

                            //- set instance selected node
                            this.selectedNode = { ..._cs, id }

                            // scroll to node
                            VueScrollTo.scrollTo('#_' + id, '100', this.scrollOptions)
                            // reset searching
                            this.setIsSearching({ isSearching: false })
                        } else {
                            //- instance selected node NOT ancestor of clicked item => unselect it & quit search if needed
                            this.selectedNode = { id: null }
                            if (!this.isSearching) {
                                this.setDisplayingSearch({ isDisplaying: false })
                            }
                        }
                    }
                }
            }
        },

        treeData: {
            handler: function(newValue, oldValue) {
                if (!isEqual(newValue, oldValue)) {
                    this.tree = newValue
                    const _selectedNode = this.getItemById({ list: newValue, id: this.selectedNode.id })
                    const id = isNil(_selectedNode.id) ? _selectedNode.key : _selectedNode.id
                    this.selectedNode = { ..._selectedNode, id }
                }
            },
            deep: true
        },

        searchedCustomSegmentParents: {
            handler: function(newValue) {
                //- if needed, update store selected node to trigger watcher
                if (this.isSearching) {
                    if (isNil(this.selectedNode.id) || !isEqual(this.selectedNode.id, this.selectedSegmentId)) {
                        for (const item of this.treeData) {
                            if (includes(newValue, item.id) || includes(newValue, item.key)) {
                                const id = isNil(item.id) ? item.key : item.id
                                this.selectedNode = { ...item, id }
                                this.setSelectedSegmentId({ id })
                                break
                            }
                        }
                    }
                }
                //- use displayingSearch boolean to display && scroll all selectedNode parents
                if (!this.displayingSearch) {
                    this.setIsSearching({ isSearching: false })
                }
            },
            deep: true
        }
    },

    mounted() {
        //- use tree mounted while searching if searched item is already displayed
        if (this.isSearching) {
            //- set store selected node on ending search display
            this.setSelectedSegmentId({ id: last(this.searchedCustomSegmentParents) })
            this.setIsSearching({ isSearching: false })
        }
        if (this.displayingSearch) {
            //- while displaying ancestors
            const scroll = scroller()
            let nodeToSelect = null
            // - find node that is one of searched item ancestors
            for (const item of this.treeData) {
                if (includes(this.searchedCustomSegmentParents, item.id)) {
                    nodeToSelect = item
                }
            }
            //- then set instance selected node
            if (nodeToSelect !== null) {
                const id = isNil(nodeToSelect.id) ? nodeToSelect.key : nodeToSelect.id
                this.selectedNode = { ...nodeToSelect, id }
                scroll('#_' + id, '100', this.scrollOptions)
            } else {
                //- if no node is found ==> stop displaying search
                this.setDisplayingSearch({ isDisplaying: false })
            }
        }
    },

    methods: {
        // Call to mutations
        ...mapMutations('ui/manageTaxonomy', [
            'setDragged',
            'setParentId',
            'setUpdateList',
            'updateTaxoCounters',
            'setIsSearching',
            'setSegmentForm',
            'setDisplayingSearch',
            'setSelectedSegmentId',
            'setNewCSParentId',
            'setCustomSegmentData',
            'setNewCSSiblings',
            'setActiveTab'
        ]),

        ...mapActions('ui/manageTaxonomy', ['tieSubItemToParent', 'deleteCustomSegment']),

        // DOM
        toggleDeleteText(element) {
            return element.action === 'delete' ? 'undelete' : 'delete'
        },

        ItemCssClasses(element) {
            let cssClasses = ''

            if (this.isHiglighted(element)) cssClasses += 'highlighted'
            if (element.isDragged) cssClasses += ' is-dragged'
            if (element.action === 'delete') cssClasses += ' is-deleted'
            if (element.action === 'update' && !element.isNew) cssClasses += ' is-updated'
            if (element.action === 'create' || (element.action === 'update' && element.isNew)) cssClasses += ' is-created'
            if (this.isSelected(element)) cssClasses += ' selected'

            return cssClasses
        },

        hasChildren(element) {
            return !isEmpty(element.children)
        },

        isSelectedFromSearch(element){
            if(element.id === this.selectedSegmentId){
                if(this.selectedNode.id
                && this.selectedNode.id !== this.selectedSegmentId
                && this.searchedCustomSegmentParents.includes(this.selectedNode.id)
                ) {
                    this.selectedNode.id = null
                }
                return 'selected'
            }
            return null
        },
        isHiglighted(node) {
            const nodeId = isNil(node.id) ? node.key : node.id
            return isEqual(nodeId, this.selectedNode.id) && this.selectedNode.id !== null && this.searchedCustomSegmentParents.includes(nodeId)
        },

        isSelected(node) {
            const nodeId = isNil(node.id) ? node.key : node.id
            return isEqual(nodeId, this.selectedNode.id) && this.selectedNode.id !== null && isEqual(nodeId, this.selectedSegmentId)
        },

        itemStatus(element) {
            if(this.elementIsSaved(element) || this.elementIsNotSaved(element)) return 'no-info'
            return element.rule.status === 'active' ? 'item-active' : 'item-inactive'
        },

        elementIsSaved(element){
            return element.isSaved && isNil(get(element, 'rule', null)) || isNil(get(element, 'rule.status', null))
        },

        elementIsNotSaved(element) {
            return isNil(get(element, 'rule', null)) || (element?.rule?.rule === '')
        },

        getOptions() {
            return {
                dragClass: 'dragging-item', // class name for the dragging item
                //forceFallback: true, //avoid shivering when item is being dragged
                ghostClass: 'dropzone', // class name for the drop placeholder
                group: 'cs', // props to enable drag between several lists,
                animation: 400,
                swapThreshold: 0.8
            }
        },

        // computed data
        childList(elem) {
            const _selectedNode = this.getItemById({ id: elem.id })
            return cloneDeep(_selectedNode.children)
        },

        // events
        checkMove(evt) {
            this.draggedNode = evt.draggedContext.element
        },

        onChange({ added }) {
            if (added) {
                this.setParentId({ item: added.element, parentId: isEmpty(this.parentId) ? null : this.parentId })
            }

            this.setUpdateList({ parentId: this.parentId, items: this.treeList })
        },

        onEnd(event) {
            if (this.hideChildren) {
                this.selectedNode = { id: '' }
            }
            if (this.draggedCounter === 0 && event.newDraggableIndex !== event.oldDraggableIndex) {
                this.updateTaxoCounters({ item: 'dragged', action: 'increase' })
            }
            if (event.newDraggableIndex !== event.oldDraggableIndex) {
                // new cs don't have id yet
                const nodeId = isNil(this.draggedNode.id) ? this.draggedNode.key : this.draggedNode.id
                this.setDragged({ nodeId })
            }
            this.draggedNode = { id: null }
            this.added = null
        },

        onChangeSubItem({ added }) {
            if (added) {
                this.added = added.element
            }
        },

        onAddSubItem(event) {
            const parentId = event.to.dataset.id
            this.tieSubItemToParent({ parentId, item: this.added })

            //update children tree (otherwise, new child not displayed when dropped into open parent)
            //this.setUpdateList({ parentId: parentId, items: this.treeList.children })
        },

        // instance methods
        selectNode(node) {
            // new cs don't have id yet
            const id = isNil(node.id) ? node.key : node.id
            //- case #1: no node selected on this tree instance OR selected by editItem()
            if (isNil(this.selectedNode.id) || this.formSegmentAction === 'update') {
                this.selectedNode = { ...node, id }
                this.setSelectedSegmentId({ id })
            } else {
                //- case #2: clicked node already selected AND its details are displayed => unselect node && display parent details
                if (isEqual(this.selectedSegmentId, id) && isEqual(this.selectedNode.id, id)) {
                    const parent = !isNil(node.parent_id) ? this.getItemById({ id: node.parent_id }) : null
                    this.selectedNode = { id: null }
                    this.setSelectedSegmentId({ id: isNil(parent) ? null : parent.id })
                }
                //- case #3: clicked node already selected AND its details are NOT displayed => display node details
                else if (!isEqual(this.selectedSegmentId, id) && isEqual(this.selectedNode.id, id)) {
                    this.setSelectedSegmentId({ id })
                }
                //- case #4: another node already selected on this instance, clicked node NOT selected => display node details
                else if (!isEqual(this.selectedSegmentId, id) && !isEqual(this.selectedNode.id, id)) {
                    this.selectedNode = { ...node, id }
                    this.setSelectedSegmentId({ id })
                }
            }

            if (this.rightPanelActiveTab !== 'information') {
                this.setActiveTab({ tab: 'information' })
            }
        },

        createCustomSegment(node) {
            const id = isNil(node.id) ? node.key : node.id
            this.setNewCSParentId({ id })
            this.setNewCSSiblings({ siblings: node.children })
            this.setSegmentForm({
                displayModal: true,
                type: 'create',
                activeTab: 'information'
            })
        },

        // CS ACTIONS: EDIT, DELETE, CREATE
        editItem(node) {
            if (!node._initial_boostrap_status) {
                node._initial_boostrap_status = node.bootstrap_status
            }
            this.setSegmentForm({
                displayModal: true,
                type: 'update',
                activeTab: 'information'
            })
            this.selectNode(node)
        },

        toggleDeleteItem(node) {
            if (node.action === 'delete') {
                node.isSaved = true
            }
            this.deleteCustomSegment({ node })
        }
    }
}
</script>

<style lang="scss" scoped>
.draggable-item {
    margin: 8px;
    width: 200px;
    border: 1px solid color('separator');
    padding: 4px;
    border-radius: 4px;
    color: color('text-inactive');

    &:hover {
        cursor: pointer;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);

        .cs-menu-action {
            display: block !important;
        }
    }
}
.sub-item {
    top: 0;
    left: 0;
    width: 200px;
    height: 100%;
    border-radius: 4px;
}

.is-dragged {
    .draggable-item--infos.label {
        &:before {
            background-color: color('accent');
        }
    }
}

.is-deleted {
    .draggable-item--infos.label {
        &:before {
            background-color: color('danger');
        }
    }
    border: 1px solid color('danger') !important;
}

.is-updated {
    .draggable-item--infos.label {
        &:before {
            background-color: color('warning');
        }
    }
}

.is-created {
    .draggable-item--infos.label {
        &:before {
            background-color: color('success');
        }
    }
}

.draggable-item--infos {
    &.id,
    &.key {
        font-size: 10px;
    }
    &.key {
        text-align: right;
        text-overflow: ellipsis;
        width: 100px;
        white-space: nowrap;
        overflow: hidden;
    }
    &.label {
        padding: 8px 0;
        color: color('text-active');
        position: relative;
        padding-left: 12px;

        &:before {
            position: absolute;
            content: '';
            top: 12px;
            left: 0;
            border-radius: 8px;
            width: 8px;
            height: 8px;
            background-color: transparent;
        }
    }
    &.rule {
        font-size: 10px;
        position: relative;

        &.item-active {
            &::after {
                background-color: color('success');
            }
        }
        &.item-inactive {
            &::after {
                background-color: color('danger');
            }
        }
        &.no-info {
            &::after {
                background-color: color('separator');
            }
        }

        &::after {
            content: '';
            width: 8px;
            height: 8px;
            border-radius: 8px;
            position: absolute;
            left: 22px;
            top: 3px;
        }
    }
}

.highlighted {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);

    .draggable-item--infos.label {
        color: color('accent');
    }
}

.selected {
    border-color: color('accent');
    .draggable-item--infos.label {
        color: color('accent');
    }
}

.clone {
    border: 2px solid rgb(5, 109, 5);
    background-color: green;
}

.hidden {
    display: none;
}

.dragging-item {
    margin: 8px;
    height: 40px;
    width: 200px;
    border: 1px solid black;
    background-color: goldenrod;
}
.dropzone {
    background-color: pink;
    border: 1px dashed grey;
}

.item_action-menu {
    @include transition;
    position: absolute;
    top: -20px;
    right: 14px;
    display: none;
    border-radius: 8px;
    background-color: white;
    box-shadow: 0 0px 10px 0 rgba(0, 0, 0, 0.2);
    font-size: 11px;
    background-color: color('background-reverse');
}

.draggable-item_add,
.draggable-item_edit {
    &:hover {
        .icon-add,
        .icon-edit {
            fill: color('accent');
        }
    }
}

.draggable-item_delete {
    &:hover {
        .icon-trash {
            fill: color('danger');
        }
    }
}

.draggable-item_action {
    z-index: 100;
    position: relative;
    width: 20px;

    &:hover {
        .icon-dots {
            fill: color('text-active');
        }
        .item_action-menu {
            display: block;
        }
    }
}

.item-action-item {
    display: flex;
    align-items: center;
    padding: 4px 12px 4px 4px;

    .icon {
        margin-right: 4px;
    }

    &:hover {
        color: color('accent');

        .icon {
            fill: color('accent');
        }
    }
    &.item-action_delete {
        &:hover {
            color: color('danger');

            .icon {
                fill: color('danger');
            }
        }
    }
}

.cs-menu-action {
    display: none !important;
    transform: rotate(90deg);
    height: 20px;
    margin-top: 6px;
}
.cs-menu-action-arrow:after {
    content: '';
    position: absolute;
    top: calc(50% - 6px);
    left: calc(100% + 5px);
    margin-left: -5px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent transparent color('background-reverse');
}

.item-children-mark {
    margin-left: 4px;
    width: 16px;
    display: flex;
    align-items: center;
}
</style>
